/**
 * Consent gate — the only place GA4 (and, through whenAnalyticsAllowed, Clarity)
 * gets loaded in the app. Runs first thing in main.tsx; replaces the inline gtag
 * that used to sit unconditionally in index.html.
 *
 * - Reads `4k_consent` (shared with 4klabs.ai on Domain=.4klabs.ai), GPC and the IANA
 *   timezone → a ConsentState (see consent.ts).
 * - Google Consent Mode v2 `default` is pushed before any `config`; `update` on a choice.
 * - Allowed (granted | notice) → gtag.js + config now; otherwise nothing loads until
 *   the visitor accepts in the bar.
 * - A choice writes 4k_consent (+ localStorage analytics_consent and the
 *   `analytics_consent_change` window event, for the landing's old consent script).
 */

import {
  CONSENT_CHANGE_EVENT,
  CONSENT_COOKIE,
  CONSENT_MAX_AGE_S,
  FIRST_TOUCH_COOKIE,
  LAST_TOUCH_COOKIE,
  LEGACY_CONSENT_KEY,
  NOTICE_COOKIE,
  SESSION_COOKIE,
  VISITOR_COOKIE,
  analyticsCookieDeletions,
  applyChoice,
  computeConsentState,
  consentModeDefault,
  consentModeRegionDefault,
  cookieDomainFor,
  decodeTouch,
  isAllowed,
  isUuid,
  parseChoice,
  parseCookies,
  serializeCookie,
  type ConsentChoice,
  type ConsentState,
  type Touch,
} from './consent';

/* eslint-disable @typescript-eslint/no-explicit-any -- must match the identical
   augmentations in workerGA4Analytics.ts / clarityAnalytics.ts */
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    clarity?: (action: string, ...args: any[]) => void;
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

type Gtag = NonNullable<Window['gtag']>;

export const GA4_MEASUREMENT_ID = 'G-B43PS4LT9C';

type Listener = (state: ConsentState) => void;

let initialized = false;
let state: ConsentState = 'pending';
let gaLoaded = false;
// URL-personalization §8.3: a provider-launched session sends nothing to our product GA4, for
// the whole tab: once any document in it opened with an opaque `actor` on its URL, later ones
// (a redirect to /login, a click-through) stay out too. The comprehension spine still records it.
const PROVIDER_SESSION_KEY = '4k_provider_session';
let providerSession = false;
const listeners = new Set<Listener>();
let pendingAllowed: Array<() => void> = [];

function lsGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function lsSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage blocked */
  }
}

function ssGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function ssSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* storage blocked */
  }
}

export function readCookies(): Record<string, string> {
  try {
    return parseCookies(document.cookie);
  } catch {
    return {};
  }
}

function writeCookie(name: string, value: string, maxAgeS: number): void {
  try {
    document.cookie = serializeCookie(name, value, {
      maxAgeS,
      domain: cookieDomainFor(window.location.hostname),
      secure: window.location.protocol === 'https:',
    });
  } catch {
    /* cookies blocked */
  }
}

function readTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

function readGpc(): boolean {
  try {
    return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
  } catch {
    return false;
  }
}

function ensureGtag(): Gtag {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // gtag.js reads the Arguments object itself, not an array copy.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
  return window.gtag;
}

function loadGa(): void {
  if (gaLoaded || providerSession) return;
  gaLoaded = true;
  const gtag = ensureGtag();
  (window as unknown as Record<string, unknown>)[`ga-disable-${GA4_MEASUREMENT_ID}`] = false;
  if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    document.head.appendChild(s);
  }
  gtag('js', new Date());
  // The load's one page_view: nothing else sends page_view.
  gtag('config', GA4_MEASUREMENT_ID);
}

function readProviderSession(): boolean {
  let launched = false;
  try {
    launched = new URLSearchParams(window.location.search).has('actor');
  } catch {
    /* no usable URL */
  }
  if (launched) ssSet(PROVIDER_SESSION_KEY, '1');
  return launched || ssGet(PROVIDER_SESSION_KEY) === '1';
}

function notify(): void {
  for (const l of listeners) {
    try {
      l(state);
    } catch {
      /* a listener must not break the gate */
    }
  }
}

function runAllowedCallbacks(): void {
  const queued = pendingAllowed;
  pendingAllowed = [];
  for (const cb of queued) {
    try {
      cb();
    } catch (err) {
      console.error('[consent] analytics start failed:', err);
    }
  }
}

/** Decide, push the Consent Mode default, and load GA4 when allowed. Idempotent. */
export function initConsentGate(): ConsentState {
  if (initialized) return state;
  initialized = true;

  let choice = parseChoice(readCookies()[CONSENT_COOKIE]);
  if (!choice) {
    // An older app-origin choice becomes the shared cookie (the landing migrates the same way).
    const legacy = parseChoice(lsGet(LEGACY_CONSENT_KEY));
    if (legacy) {
      choice = legacy;
      writeCookie(CONSENT_COOKIE, legacy, CONSENT_MAX_AGE_S);
    }
  }
  state = computeConsentState({ gpc: readGpc(), choice, tz: readTimezone() });
  providerSession = readProviderSession();
  if (providerSession) (window as unknown as Record<string, unknown>)[`ga-disable-${GA4_MEASUREMENT_ID}`] = true;

  const gtag = ensureGtag();
  gtag('consent', 'default', consentModeDefault(state));
  const regional = consentModeRegionDefault(state);
  if (regional) gtag('consent', 'default', regional);
  if (isAllowed(state)) loadGa();
  else dropAnalyticsCookies();
  return state;
}

function dropAnalyticsCookies(): void {
  try {
    for (const c of analyticsCookieDeletions(Object.keys(readCookies()), window.location.hostname)) {
      document.cookie = c;
    }
  } catch {
    /* cookies blocked */
  }
}

export function getConsentState(): ConsentState {
  return initialized ? state : initConsentGate();
}

export function isAnalyticsAllowed(): boolean {
  return isAllowed(getConsentState());
}

/** GA4 specifically: allowed by consent, and not a provider-launched session. */
export function isGa4Allowed(): boolean {
  return isAnalyticsAllowed() && !providerSession;
}

/** Run `cb` now if analytics is allowed, else once the visitor accepts (never on decline). */
export function whenAnalyticsAllowed(cb: () => void): void {
  pendingAllowed.push(cb);
  if (isAnalyticsAllowed()) runAllowedCallbacks();
}

export function subscribeConsent(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Accept / Decline in the bar, or "Opt out" in the notice. */
export function setConsentChoice(choice: ConsentChoice): void {
  getConsentState();
  state = applyChoice(state, choice);
  writeCookie(CONSENT_COOKIE, choice, CONSENT_MAX_AGE_S);
  lsSet(LEGACY_CONSENT_KEY, choice);

  const allowed = isAllowed(state);
  ensureGtag()('consent', 'update', { analytics_storage: allowed ? 'granted' : 'denied' });
  if (allowed) {
    loadGa();
    runAllowedCallbacks();
  } else {
    // Stop sending for the rest of this page, end Clarity's session (it erases its
    // cookies), and drop every analytics cookie — as the landing does on opt-out.
    (window as unknown as Record<string, unknown>)[`ga-disable-${GA4_MEASUREMENT_ID}`] = true;
    try {
      window.clarity?.('consent', false);
    } catch {
      /* clarity not loaded */
    }
    dropAnalyticsCookies();
  }

  try {
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: { consent: choice } }));
  } catch {
    /* old browser */
  }
  notify();
}

export function isNoticeDismissed(): boolean {
  return readCookies()[NOTICE_COOKIE] === '1';
}

/** A notice-region visitor closed the one-time notice. */
export function dismissNotice(): void {
  writeCookie(NOTICE_COOKIE, '1', CONSENT_MAX_AGE_S);
  notify();
}

/** GA4 event, only when GA4 is allowed. */
export function gaEvent(name: string, params?: Record<string, unknown>): void {
  if (!isGa4Allowed()) return;
  window.gtag?.('event', name, params ?? {});
}

/** The landing's visitor cookies — only read when analytics is allowed. */
export function readVisitorCookies(): { visitorId: string | null; sid: string | null; ft: Touch | null; lt: Touch | null } {
  if (!isAnalyticsAllowed()) return { visitorId: null, sid: null, ft: null, lt: null };
  const c = readCookies();
  return {
    visitorId: isUuid(c[VISITOR_COOKIE]) ? c[VISITOR_COOKIE] : null,
    sid: isUuid(c[SESSION_COOKIE]) ? c[SESSION_COOKIE] : null,
    ft: decodeTouch(c[FIRST_TOUCH_COOKIE]),
    lt: decodeTouch(c[LAST_TOUCH_COOKIE]),
  };
}
